import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../../components/breadcrumbs'
import Footer from '../../../components/footer'
import { getCanonicalUrl } from '../../../utils/canonical'
import DarkNav from '../../../components/dark-nav'
import './zapier-feature.css'

const CategoriseSmsAI = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier', path: '/integrations/zapier' },
    { label: 'Categorise SMS With AI' }
  ]

  const useCases = [
    {
      title: "Automated Customer Support Ticket Routing",
      industry: "Customer Support",
      description: "Automatically categorise incoming Australian customer SMS messages and route to the correct department or support team.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'My internet has been down for 3 hours!'",
        "AI categorises message → Category: Technical Support, Subcategory: Internet Outage, Urgency: High",
        "Create Zendesk ticket in 'Technical Support' queue with High priority",
        "Assign to on-call technical team member",
        "Send Slack alert to #tech-support channel",
        "Auto-reply SMS: 'Technical support notified. We'll call you within 15 minutes.'"
      ]
    },
    {
      title: "Product Enquiry Classification for Retail",
      industry: "Retail & E-commerce",
      description: "Classify Australian customer product enquiries by category, intent, and priority for better sales response.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'Do you have the Nike Air Max in size 10?'",
        "AI categorises → Category: Product Enquiry, Product Type: Footwear, Brand: Nike, Intent: Purchase",
        "Check inventory system for Nike Air Max size 10",
        "If in stock: SMS with availability and store location",
        "If out of stock: SMS with alternatives and restock date",
        "Log enquiry in Salesforce with category tags"
      ]
    },
    {
      title: "Medical Appointment Request Categorisation",
      industry: "Healthcare",
      description: "Automatically categorise patient SMS requests by urgency, department, and appointment type for Australian healthcare practices.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Patient sends SMS: 'I need to see Dr. Smith about my test results'",
        "AI categorises → Category: Appointment Request, Doctor: Dr. Smith, Type: Follow-up, Urgency: Medium",
        "Check Dr. Smith's calendar for next available follow-up slot",
        "Send SMS: 'Dr. Smith available Thursday 2:30 PM or Friday 10 AM. Reply 1 for Thu or 2 for Fri.'",
        "Patient reply triggers booking confirmation",
        "Add to practice management system with category tags"
      ]
    },
    {
      title: "Real Estate Enquiry Segmentation",
      industry: "Real Estate",
      description: "Categorise Australian property enquiries by type, budget range, and urgency to prioritise agent follow-up.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Prospect sends SMS: 'Interested in 123 Smith St. Looking for 3BR under $1M in Sydney'",
        "AI categorises → Category: Property Enquiry, Property: 123 Smith St, Bedrooms: 3, Budget: Under $1M, Location: Sydney, Intent: Serious Buyer",
        "Assign lead score based on category (3BR + $1M budget = High value)",
        "Alert agent via SMS with prospect details and category tags",
        "Add to CRM with tags: Budget: $1M, Location: Sydney, Urgency: High",
        "Agent calls prospect within 5 minutes"
      ]
    },
    {
      title: "Event Registration Category Routing",
      industry: "Events & Conferences",
      description: "Categorise event-related SMS enquiries to route to tickets, venue, catering, or logistics teams.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Attendee sends SMS: 'I have a dietary requirement - gluten free. How do I update my registration?'",
        "AI categorises → Category: Event Enquiry, Topic: Dietary Requirements, Department: Catering, Urgency: Medium",
        "Create task for catering team in Asana",
        "Send SMS: 'Thanks! Our catering team will email you a dietary form within the hour.'",
        "Notify catering@event.com with attendee details",
        "Log in event management system under 'Catering Requests'"
      ]
    },
    {
      title: "Restaurant Reservation & Enquiry Categorisation",
      industry: "Hospitality",
      description: "Automatically categorise Australian restaurant SMS messages by type: bookings, menu enquiries, feedback, or complaints.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'Can I book a table for 6 people Saturday night?'",
        "AI categorises → Category: Reservation Request, Party Size: 6, Date: Saturday, Intent: Book",
        "Check OpenTable for Saturday availability",
        "Send SMS: 'Available at 6:30 PM or 8:00 PM Saturday. Reply 1 for 6:30 or 2 for 8:00.'",
        "Customer reply triggers automatic booking creation",
        "Confirmation SMS sent with booking details"
      ]
    },
    {
      title: "Feedback & Complaint Categorisation System",
      industry: "Customer Experience",
      description: "Categorise Australian customer feedback by type, sentiment, and department for better response management.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'Your delivery driver was rude and my order was cold!'",
        "AI categorises → Category: Complaint, Topic: Delivery Service + Food Quality, Sentiment: Negative, Urgency: High",
        "Create priority support ticket in Zendesk",
        "Alert customer success manager via Slack",
        "Auto-reply SMS: 'We're very sorry! Manager will call you within 10 minutes.'",
        "Log in complaints dashboard for trend analysis"
      ]
    },
    {
      title: "Lead Source & Intent Classification",
      industry: "Sales & Marketing",
      description: "Categorise inbound Australian sales enquiries by product interest, budget, and purchase intent for better lead qualification.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Lead sends SMS: 'I saw your ad for enterprise software. Need solution for 50+ users. Budget approved.'",
        "AI categorises → Category: Sales Enquiry, Product: Enterprise Software, Company Size: 50+ users, Budget Status: Approved, Intent: High",
        "Calculate lead score: Enterprise (5 pts) + 50 users (4 pts) + Budget approved (5 pts) = 14/15 (Hot Lead)",
        "Immediately assign to senior sales rep",
        "Alert via phone call + SMS to sales rep",
        "Create Salesforce opportunity with category: Hot Lead - Enterprise"
      ]
    },
    {
      title: "Delivery Issue Categorisation for Logistics",
      industry: "Logistics & Delivery",
      description: "Automatically categorise delivery-related SMS messages by issue type, urgency, and required action.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'Package not delivered today. Tracking says delivered but I never received it.'",
        "AI categorises → Category: Delivery Issue, Type: Missing Package, Urgency: High, Action Required: Investigation",
        "Create urgent case in delivery management system",
        "Alert local depot manager and driver",
        "Send SMS: 'Investigating immediately. Depot manager will call within 30 minutes.'",
        "Escalate to senior ops if not resolved in 2 hours"
      ]
    },
    {
      title: "Financial Enquiry Categorisation for Banking",
      industry: "Banking & Finance",
      description: "Categorise Australian customer banking SMS enquiries by type, account, and urgency for compliance and routing.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'I see a $500 charge I don't recognise on my credit card'",
        "AI categorises → Category: Fraud Concern, Account Type: Credit Card, Amount: $500, Urgency: Critical",
        "Immediately flag account for fraud review",
        "Create high-priority case in fraud prevention system",
        "Send SMS: 'Security team notified. Freezing card now. We'll call you in 5 minutes.'",
        "Alert fraud team via phone + email with category: Potential Fraud - Credit Card"
      ]
    },
    {
      title: "IT Help Desk Ticket Auto-Categorisation",
      industry: "IT & Managed Services",
      description: "Automatically categorise IT support requests from Australian clients by issue type, severity, and SLA priority.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Client sends SMS: 'Email server down for entire Sydney office - 40 staff affected'",
        "AI categorises → Category: IT Issue, Type: Email Server, Severity: Critical, Affected Users: 40, Location: Sydney Office",
        "Create P1 (Priority 1) ticket in ServiceNow",
        "Alert on-call engineer via phone + SMS",
        "Notify client: 'P1 ticket created. Engineer John responding now. ETA: 10 minutes.'",
        "Escalate to senior engineer if not resolved in 30 minutes per SLA"
      ]
    },
    {
      title: "Educational Enquiry Categorisation for Schools",
      industry: "Education",
      description: "Categorise parent/student enquiries for Australian schools by topic, grade level, and department.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Parent sends SMS: 'My daughter is sick today. Year 7. Won't be at school.'",
        "AI categorises → Category: Absence Notification, Student: Year 7, Reason: Illness, Department: Attendance Office",
        "Log absence in school management system",
        "Notify Year 7 homeroom teacher",
        "Send SMS confirmation: 'Absence recorded. Hope she feels better soon!'",
        "Add to attendance report for the day"
      ]
    }
  ]

  return (
    <div className="zapier-feature-container">
      <DarkNav />
      <Helmet>
        <title>Categorise SMS With AI via Zapier - DataFlows SMS Integration Australia</title>
        <meta
          name="description"
          content="Automatically categorise SMS messages with AI in Zapier. Route support tickets, classify enquiries, and prioritise messages by intent. Australian SMS AI automation."
        />
        <meta
          property="og:title"
          content="Categorise SMS With AI via Zapier - DataFlows SMS Integration Australia"
        />
        <meta
          property="og:description"
          content="Automatically categorise SMS messages with AI in Zapier. Route support tickets, classify enquiries, and prioritise messages by intent. Australian SMS AI automation."
        />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier/actions/categorise-sms-ai')} />
      </Helmet>

      <div className="zapier-feature-hero" data-aos="fade-down">
        <div className="breadcrumbs-wrapper">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        <h1 className="zapier-feature-title">Categorise SMS With AI</h1>
        <p className="zapier-feature-subtitle">
          Automatically categorise and classify SMS messages using artificial intelligence. Perfect for Australian businesses needing smart message routing, support ticket categorisation, and intent detection.
        </p>
        <div className="zapier-feature-badge">
          <span className="badge-type">Action</span>
          <span className="badge-separator">•</span>
          <span className="badge-plan">Pro & Enterprise Plans</span>
        </div>
      </div>

      <div className="zapier-feature-content">
        {/* Overview Section */}
        <section className="feature-section" data-aos="fade-up">
          <h2>What Does This Action Do?</h2>
          <p className="feature-description">
            The <strong>Categorise SMS With AI</strong> action uses advanced artificial intelligence to automatically classify incoming SMS messages into categories, topics, intents, and urgency levels. Perfect for Australian businesses that need to route customer messages to the right team, prioritise urgent requests, or automate support workflows based on message content.
          </p>
          <div className="feature-highlight">
            <p><strong>Popular Australian Use Cases:</strong> Support ticket routing, product enquiry classification, appointment request categorisation, lead qualification, complaint detection, delivery issue sorting, and department-based message routing.</p>
          </div>
        </section>

        {/* How It Works */}
        <section className="feature-section" data-aos="fade-up">
          <h2>How It Works</h2>
          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>SMS Received as Trigger</h3>
                <p>Use the "New SMS Received" trigger to capture incoming customer messages. Each SMS is passed to the AI categorisation action.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>AI Analyses Message Content</h3>
                <p>DataFlows AI reads the SMS text and intelligently categorises it based on your custom category definitions or our pre-built templates.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Returns Category & Metadata</h3>
                <p>Action outputs: Primary Category, Subcategory, Intent, Urgency Level, Confidence Score, and Suggested Action. Use these in subsequent Zap steps.</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Route & Automate Based on Category</h3>
                <p>Use Zapier Paths/Filters to route messages: High urgency → Call manager, Product enquiry → Sales CRM, Complaint → Support ticket, etc.</p>
              </div>
            </div>
          </div>
        </section>

        {/* AI Output Fields */}
        <section className="feature-section" data-aos="fade-up">
          <h2>AI Categorisation Output Fields</h2>
          <div className="config-fields">
            <div className="config-field">
              <h4>Primary Category</h4>
              <p>The main classification of the message. Examples: "Support Request", "Product Enquiry", "Complaint", "Appointment Request", "Feedback", "Sales Lead".</p>
            </div>
            <div className="config-field">
              <h4>Subcategory</h4>
              <p>More specific classification within primary category. Examples: "Technical Issue - Internet", "Billing Question", "Delivery Concern", "Urgent Medical".</p>
            </div>
            <div className="config-field">
              <h4>Intent</h4>
              <p>Customer's underlying intention. Examples: "Purchase", "Get Information", "Report Problem", "Request Callback", "Cancel Service", "Provide Feedback".</p>
            </div>
            <div className="config-field">
              <h4>Urgency Level</h4>
              <p>Assessed priority: "Critical", "High", "Medium", "Low". Helps prioritise response. Critical = immediate action needed, Low = can wait 24+ hours.</p>
            </div>
            <div className="config-field">
              <h4>Confidence Score</h4>
              <p>AI's certainty in the categorisation (0.0 to 1.0). Higher = more confident. Use to filter: only auto-route if confidence {'>'} 0.8, otherwise escalate to human.</p>
            </div>
            <div className="config-field">
              <h4>Suggested Action</h4>
              <p>AI's recommendation for next step. Examples: "Create support ticket", "Assign to sales team", "Schedule callback", "Offer refund", "Escalate to manager".</p>
            </div>
            <div className="config-field">
              <h4>Detected Topics</h4>
              <p>Key topics/keywords identified. Examples: "Billing, Late Payment", "Product: Nike Air Max, Size 10", "Complaint, Rude Staff, Refund Request". Useful for tagging.</p>
            </div>
            <div className="config-field">
              <h4>Department</h4>
              <p>Recommended department for routing. Examples: "Technical Support", "Sales", "Billing", "Customer Success", "Warehouse", "HR". Based on your organisation structure.</p>
            </div>
          </div>
        </section>

        {/* Configuration */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Action Configuration Options</h2>
          <div className="config-fields">
            <div className="config-field">
              <h4>Message Text <span className="required-badge">Required</span></h4>
              <p>The SMS message content to categorise. Typically mapped from "New SMS Received" trigger: {'{'}{'{'} Message Text {'}'}{'}' }.</p>
            </div>
            <div className="config-field">
              <h4>Category Template <span className="optional-badge">Optional</span></h4>
              <p>Choose from pre-built templates: "Customer Support", "Retail Enquiries", "Healthcare", "Real Estate", "General Business". Or create custom categories.</p>
            </div>
            <div className="config-field">
              <h4>Custom Categories <span className="optional-badge">Optional</span></h4>
              <p>Define your own category list (comma-separated): "Technical Support, Billing Question, Sales Enquiry, Complaint, Feedback". AI will classify into these.</p>
            </div>
            <div className="config-field">
              <h4>Include Sentiment <span className="optional-badge">Optional</span></h4>
              <p>Also run sentiment analysis alongside categorisation. Returns: Positive/Negative/Neutral + Emotion. Useful for complaint detection.</p>
            </div>
            <div className="config-field">
              <h4>Language <span className="optional-badge">Optional</span></h4>
              <p>Message language. Defaults to "English (Australian)". Also supports: English (US/UK), Mandarin, Vietnamese, Arabic, Spanish. AI adjusts for dialects.</p>
            </div>
          </div>
        </section>

        {/* Real-World Use Cases */}
        <section className="feature-section use-cases-section" data-aos="fade-up">
          <h2>Real-World Australian Use Cases</h2>
          <p className="section-intro">
            Here are {useCases.length} proven ways Australian businesses use AI categorisation to automate message routing and improve response times:
          </p>

          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-card" data-aos="fade-up" data-aos-delay={index * 50}>
              <div className="use-case-header">
                <h3>{useCase.title}</h3>
                <span className="industry-tag">{useCase.industry}</span>
              </div>
              <p className="use-case-description">{useCase.description}</p>
              <div className="use-case-trigger">
                <strong>Trigger:</strong> {useCase.trigger}
              </div>
              <div className="workflow-breakdown">
                <h4>Workflow Breakdown:</h4>
                <ol className="workflow-steps-list">
                  {useCase.steps.map((step, stepIndex) => (
                    <li key={stepIndex}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          ))}
        </section>

        {/* Setup Guide */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Quick Setup Guide</h2>
          <div className="setup-steps">
            <h3>Step 1: Set Up New SMS Received Trigger</h3>
            <ol>
              <li>Create new Zap in Zapier</li>
              <li>Search for "DataFlows SMS" and select it</li>
              <li>Choose trigger: "New SMS Received"</li>
              <li>Connect your DataFlows account</li>
              <li>Test trigger to get sample SMS data</li>
            </ol>

            <h3>Step 2: Add Categorise SMS With AI Action</h3>
            <ol>
              <li>Click "+" to add action step</li>
              <li>Search for "DataFlows SMS" and select it</li>
              <li>Choose action: "Categorise SMS With AI"</li>
              <li>Your DataFlows account should already be connected</li>
              <li>Click "Continue"</li>
            </ol>

            <h3>Step 3: Configure AI Categorisation</h3>
            <ol>
              <li><strong>Message Text:</strong> Map the message from trigger: {'{'}{'{'} Message Text {'}'}{'}' }</li>
              <li><strong>Category Template:</strong> Choose pre-built template or select "Custom"</li>
              <li>If using custom categories, enter your list:
                <div className="code-example">
                  Technical Support, Billing Question, Sales Enquiry, Product Question, Complaint, General Feedback
                </div>
              </li>
              <li><strong>Include Sentiment:</strong> Toggle ON if you want emotion detection too</li>
              <li><strong>Language:</strong> Select "English (Australian)" or leave default</li>
              <li>Click "Continue"</li>
            </ol>

            <h3>Step 4: Test AI Categorisation</h3>
            <ol>
              <li>Click "Test action" to run AI on your sample SMS</li>
              <li>Review the output fields:
                <ul>
                  <li>Primary Category (e.g., "Support Request")</li>
                  <li>Subcategory (e.g., "Technical Issue - Internet")</li>
                  <li>Intent (e.g., "Report Problem")</li>
                  <li>Urgency Level (e.g., "High")</li>
                  <li>Confidence Score (e.g., 0.91)</li>
                  <li>Suggested Action (e.g., "Create urgent support ticket")</li>
                </ul>
              </li>
              <li>Verify the AI categorised correctly. If not, refine your custom categories.</li>
            </ol>

            <h3>Step 5: Add Routing Logic Based on Category</h3>
            <ol>
              <li>Click "+" to add next step</li>
              <li>Choose "Paths by Zapier" for conditional routing based on category</li>
              <li><strong>Path A: High Urgency Messages</strong>
                <ul>
                  <li>Condition: Urgency Level = "Critical" OR "High"</li>
                  <li>Actions: Create urgent ticket + Alert manager via phone call + SMS customer</li>
                </ul>
              </li>
              <li><strong>Path B: Sales Enquiries</strong>
                <ul>
                  <li>Condition: Primary Category = "Sales Lead" OR "Product Enquiry"</li>
                  <li>Actions: Create Salesforce lead + Assign to sales rep + SMS confirmation</li>
                </ul>
              </li>
              <li><strong>Path C: Complaints</strong>
                <ul>
                  <li>Condition: Primary Category = "Complaint" OR Sentiment = "Negative"</li>
                  <li>Actions: Escalate to customer success + Offer apology + Priority follow-up</li>
                </ul>
              </li>
              <li><strong>Path D: General Enquiries</strong>
                <ul>
                  <li>Condition: Everything else (Urgency = "Low" or "Medium")</li>
                  <li>Actions: Create standard ticket + SMS with expected response time</li>
                </ul>
              </li>
            </ol>

            <h3>Step 6: Activate & Monitor</h3>
            <ol>
              <li>Turn on your Zap</li>
              <li>Monitor Zap history to see how messages are being categorised</li>
              <li>Check categorisation accuracy after first 50-100 messages</li>
              <li>Refine custom categories if needed based on real data</li>
              <li>Track metrics: Average confidence score, category distribution, routing effectiveness</li>
            </ol>
          </div>
        </section>

        {/* Best Practices */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Best Practices for Australian Businesses</h2>
          <div className="best-practices">
            <div className="practice-item">
              <h4>✅ Start With Pre-Built Templates</h4>
              <p>Use DataFlows' pre-built category templates for your industry first. They're trained on thousands of real messages. Customise later if needed.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Keep Custom Categories Clear & Distinct</h4>
              <p>Define 5-10 categories maximum with clear differences. Avoid overlap: "Technical Support" and "IT Issues" are too similar. AI works best with distinct categories.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Use Confidence Score for Human Escalation</h4>
              <p>Only auto-route messages with confidence {'>'}= 0.75. Lower confidence? Send to human for manual categorisation. Prevents mis-routing critical messages.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Combine with Sentiment Analysis</h4>
              <p>Enable "Include Sentiment" to catch angry customers even if category is neutral. Example: "Product Enquiry" + "Negative Sentiment" = Unhappy customer, prioritise!</p>
            </div>
            <div className="practice-item">
              <h4>✅ Route Critical Urgency to Humans Immediately</h4>
              <p>When Urgency = "Critical", bypass automation and alert humans via phone/SMS. Examples: "Server down", "Medical emergency", "Security breach".</p>
            </div>
            <div className="practice-item">
              <h4>✅ Log All Categorisations for Analysis</h4>
              <p>Send category data to Google Sheets or Airtable: Message, Category, Confidence, Timestamp. Helps identify trends: "50% of messages are billing questions - need FAQ".</p>
            </div>
            <div className="practice-item">
              <h4>✅ Test with Real Australian Language</h4>
              <p>AI is trained on Australian English ("mobile" not "cell", "organisation" not "organization"). Test with real Aussie customer messages for accuracy.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Use Suggested Action Field</h4>
              <p>Don't ignore this! AI recommends next steps based on message content. Use in Paths: If Suggested Action = "Escalate to manager", trigger manager alert.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Review & Refine Categories Monthly</h4>
              <p>After 30 days, analyse category distribution. If 80% fall into "Other" or "General", your categories are too specific. Adjust based on real data patterns.</p>
            </div>
            <div className="practice-item">
              <h4>✅ Handle Multi-Topic Messages</h4>
              <p>Some messages cover multiple topics: "I want to buy your product but I'm also unhappy with previous purchase". Check Detected Topics field for all themes.</p>
            </div>
          </div>
        </section>

        {/* Advanced Tips */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Advanced Automation Tips</h2>
          <div className="advanced-tips">
            <h3>Multi-Tier Routing Based on Category + Sentiment</h3>
            <p>Create sophisticated routing logic combining multiple AI outputs:</p>
            <ul>
              <li><strong>Tier 1 (Immediate):</strong> Urgency = Critical OR (Category = Complaint AND Sentiment = Negative)</li>
              <li><strong>Tier 2 (2 hour SLA):</strong> Urgency = High OR Category = Sales Lead</li>
              <li><strong>Tier 3 (24 hour SLA):</strong> Urgency = Medium AND Sentiment = Neutral</li>
              <li><strong>Tier 4 (48 hour SLA):</strong> Urgency = Low AND Category = General Enquiry</li>
            </ul>

            <h3>Dynamic CRM Field Population</h3>
            <p>Use category data to auto-populate CRM fields:</p>
            <ul>
              <li>Primary Category → Salesforce Lead Source</li>
              <li>Detected Topics → HubSpot Contact Tags</li>
              <li>Urgency Level → Support Ticket Priority</li>
              <li>Intent → Pipeline Stage (Purchase Intent = "Hot Lead" stage)</li>
            </ul>

            <h3>Category-Based Auto-Responses</h3>
            <p>Send different SMS replies based on category:</p>
            <ul>
              <li>Support Request → "Ticket #1234 created. Response in 2 hours."</li>
              <li>Sales Enquiry → "Thanks! Sales rep will call within 30 mins."</li>
              <li>Billing Question → "Billing team notified. Reply in 24 hours."</li>
              <li>Complaint → "We're very sorry! Manager will call you within 15 minutes."</li>
            </ul>

            <h3>AI Training Feedback Loop</h3>
            <p>Improve AI accuracy over time:</p>
            <ul>
              <li>Log all categorisations to Google Sheets with columns: Message, AI Category, Human Review, Correct?</li>
              <li>Staff review and mark incorrect categorisations</li>
              <li>Weekly: Identify patterns in mis-categorisations</li>
              <li>Adjust custom category definitions based on errors</li>
              <li>Re-test with historical messages to verify improvement</li>
            </ul>

            <h3>Department-Specific Zaps</h3>
            <p>Create separate Zaps for each department based on category routing:</p>
            <ul>
              <li><strong>Sales Zap:</strong> Filter for Category = Sales Lead → Salesforce → Assign rep → Call within 5 mins</li>
              <li><strong>Support Zap:</strong> Filter for Category = Support → Zendesk → Route by subcategory → SLA timer</li>
              <li><strong>Billing Zap:</strong> Filter for Category = Billing → Xero lookup → Send account statement SMS</li>
            </ul>

            <h3>Compliance & Audit Trail</h3>
            <p>For regulated industries (healthcare, finance):</p>
            <ul>
              <li>Log every message + category + confidence to immutable database</li>
              <li>Include timestamp, staff assigned, response time</li>
              <li>Use Category = "Compliance Risk" to trigger legal review</li>
              <li>Monthly reports: Category trends, response times by urgency, escalation rates</li>
            </ul>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="feature-section" data-aos="fade-up">
          <h2>Common Issues & Solutions</h2>
          <div className="troubleshooting">
            <div className="issue-solution">
              <h4>❌ Low Confidence Scores ({'<'} 0.5)</h4>
              <p><strong>Solutions:</strong></p>
              <ul>
                <li>Your custom categories may be too similar or ambiguous. Make them more distinct.</li>
                <li>Messages are too short or vague ("Hi", "Thanks") - add filter to require minimum 10 characters</li>
                <li>Switch to pre-built template instead of custom categories</li>
                <li>Route low-confidence messages to human review instead of auto-processing</li>
              </ul>
            </div>
            <div className="issue-solution">
              <h4>❌ AI Choosing Wrong Category Consistently</h4>
              <p><strong>Solutions:</strong></p>
              <ul>
                <li>Review your category definitions - are they clear and distinct?</li>
                <li>Reduce number of categories (aim for 5-8 max)</li>
                <li>Provide more context: Include previous message history if available</li>
                <li>Use pre-built industry template which has better training data</li>
              </ul>
            </div>
            <div className="issue-solution">
              <h4>❌ Most Messages Categorised as "General" or "Other"</h4>
              <p><strong>Solution:</strong> Your categories are too specific. Broaden them. Instead of "Technical Issue - Internet - Router Not Working", use "Technical Support". Let subcategory handle specifics.</p>
            </div>
            <div className="issue-solution">
              <h4>❌ Non-English Messages Not Categorising Well</h4>
              <p><strong>Solution:</strong> Set the Language field to match your messages (Mandarin, Vietnamese, Arabic, etc.). AI accuracy drops significantly if language mismatch.</p>
            </div>
            <div className="issue-solution">
              <h4>❌ Urgency Always Returns "Medium"</h4>
              <p><strong>Note:</strong> AI assesses urgency based on message content keywords ("urgent", "asap", "emergency", "broken", "not working"). If customers don't use urgency language, most will be "Medium". Consider using sentiment as additional urgency signal.</p>
            </div>
          </div>
        </section>

        {/* Related Features */}
        <section className="feature-section related-features" data-aos="fade-up">
          <h2>Related DataFlows Features</h2>
          <div className="related-cards">
            <Link to="/integrations/zapier/actions/analyse-sentiment-ai" className="related-card">
              <h4>Analyse SMS Sentiment With AI</h4>
              <p>Detect customer emotions (happy, angry, frustrated) in SMS messages. Perfect for complaint detection and customer experience monitoring.</p>
              <span className="feature-type">Action</span>
            </Link>
            <Link to="/integrations/zapier/triggers/new-sms-received" className="related-card">
              <h4>New SMS Received</h4>
              <p>Trigger your Zaps when customers send SMS messages. The starting point for AI categorisation workflows.</p>
              <span className="feature-type">Trigger</span>
            </Link>
            <Link to="/integrations/zapier/actions/send-sms" className="related-card">
              <h4>Send SMS</h4>
              <p>Send automated SMS responses based on message category. Perfect for category-specific auto-replies.</p>
              <span className="feature-type">Action</span>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="feature-section cta-section" data-aos="fade-up">
          <div className="cta-content">
            <h2>Ready to Automate Message Categorisation With AI?</h2>
            <p>Join Australian businesses using DataFlows AI to automatically route, prioritise, and categorise thousands of SMS messages every day.</p>
            <div className="cta-buttons">
              <a
                href="https://zapier.com/apps/dataflows-sms/integrations"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Get Started with Zapier
              </a>
              <Link to="/integrations/zapier" className="btn-secondary">
                View All Features
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-to-top"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  )
}

export default CategoriseSmsAI
